import { useEffect, useState } from "preact/hooks"
import { api } from "../lib/api"
import { Spinner } from "./Spinner"

interface LoginProps {
	onAuthed: () => void
}

type Mode = "login" | "signup" | "verify" | "forgot" | "reset"

// Email/password auth, ported from ~/labs/lilac/frontend/components/Login.tsx
// (Google OAuth is gone). Unlike lilac, which routes login/signup/verify as
// separate top-level pages, specs/plan.md keeps all of this under the single
// /login route — so mode is local component state, not the URL, and
// switching between them uses plain buttons rather than <a href> (no actual
// navigation is happening). lilac's own frontend never implemented UI for
// forgot/reset-password or a resend-code control even though its backend
// supports them (auth.go), so those two panels below are original, built
// against the confirmed endpoint contract, not ported from an existing UI.
export function Login({ onAuthed }: LoginProps) {
	const [mode, setMode] = useState<Mode>("login")
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [code, setCode] = useState("")
	const [newPassword, setNewPassword] = useState("")
	const [pendingEmail, setPendingEmail] = useState("")
	const [error, setError] = useState("")
	const [info, setInfo] = useState("")
	const [loading, setLoading] = useState(false)

	// Invite emails link here as /login?email=…[&signup=1] so the invitee
	// lands with their address pre-filled, and — for a brand-new invitee
	// who has no account yet — already on the signup form rather than the
	// login form. Parse once on mount; mode/email are local state after.
	// biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const e = params.get("email")
		if (e) {
			setEmail(e)
			if (params.has("signup")) {
				setMode("signup")
			}
		}
	}, [])

	const goto = (m: Mode) => {
		setMode(m)
		setError("")
		setInfo("")
	}

	const handleLogin = async (e: Event) => {
		e.preventDefault()
		setError("")
		setLoading(true)
		try {
			const result = await api.login(email, password)
			if (result.kind === "verification_required") {
				setPendingEmail(result.email)
				setCode("")
				goto("verify")
			} else {
				onAuthed()
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to log in")
		} finally {
			setLoading(false)
		}
	}

	const handleSignup = async (e: Event) => {
		e.preventDefault()
		setError("")
		setLoading(true)
		try {
			const result = await api.signup(email, password)
			if (result.kind === "verification_required") {
				setPendingEmail(result.email)
				setCode("")
				goto("verify")
			} else {
				onAuthed()
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to sign up")
		} finally {
			setLoading(false)
		}
	}

	const handleVerify = async (e: Event) => {
		e.preventDefault()
		setError("")
		setLoading(true)
		try {
			await api.verifyEmail(pendingEmail, code)
			onAuthed()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Invalid code")
		} finally {
			setLoading(false)
		}
	}

	const handleResend = async () => {
		setError("")
		setInfo("")
		setLoading(true)
		try {
			await api.resendCode(pendingEmail)
			setInfo("Code resent. Check your email.")
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to resend code")
		} finally {
			setLoading(false)
		}
	}

	const handleForgot = async (e: Event) => {
		e.preventDefault()
		setError("")
		setLoading(true)
		try {
			await api.forgotPassword(email)
			setPendingEmail(email)
			setCode("")
			setNewPassword("")
			setInfo("If that email has an account, we sent a reset code.")
			setMode("reset")
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to send reset code")
		} finally {
			setLoading(false)
		}
	}

	const handleReset = async (e: Event) => {
		e.preventDefault()
		setError("")
		setLoading(true)
		try {
			await api.resetPassword(pendingEmail, code, newPassword)
			onAuthed()
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to reset password")
		} finally {
			setLoading(false)
		}
	}

	const submitIcon = loading ? (
		<Spinner size={16} />
	) : (
		<i
			class={
				mode === "verify" || mode === "reset"
					? "bi bi-shield-check"
					: mode === "signup"
						? "bi bi-person-plus"
						: mode === "forgot"
							? "bi bi-envelope"
							: "bi bi-box-arrow-in-right"
			}
			aria-hidden="true"
		/>
	)

	return (
		<div class="flex-grow-1 overflow-auto">
			<div class="container-fluid px-3">
				<div class="row justify-content-center">
					<div class="col-12 col-sm-8 col-md-5 col-lg-4">
						<div class="text-center mt-5 mt-sm-4 mb-3">
							<img
								src="/static/assets/logo.png"
								alt=""
								width="96"
								height="94"
								class="mb-2"
							/>
							<h1 class="brand-name mb-0">Artbox</h1>
							<p class="text-body-secondary">
								A family scrapbook for the art your kids make.
							</p>
						</div>

						<div>
							<div>
								<h2 class="h5 mb-3">
									{mode === "login" && "Log in"}
									{mode === "signup" && "Sign up"}
									{mode === "verify" && "Verify your email"}
									{mode === "forgot" && "Reset your password"}
									{mode === "reset" && "Choose a new password"}
								</h2>

								{error && (
									<div class="alert alert-danger py-2 small">{error}</div>
								)}
								{info && <div class="alert alert-info py-2 small">{info}</div>}

								{mode === "login" && (
									<form onSubmit={handleLogin} class="vstack gap-3">
										<div>
											<label class="form-label">Email</label>
											<input
												type="email"
												class="form-control"
												value={email}
												onInput={(e) =>
													setEmail((e.target as HTMLInputElement).value)
												}
												required
											/>
										</div>
										<div>
											<label class="form-label">Password</label>
											<input
												type="password"
												class="form-control"
												value={password}
												onInput={(e) =>
													setPassword((e.target as HTMLInputElement).value)
												}
												required
												minLength={4}
											/>
										</div>
										<button
											type="submit"
											class="btn btn-primary w-100"
											disabled={loading}
										>
											{submitIcon}
											<span>{loading ? "Working…" : "Log in"}</span>
										</button>
									</form>
								)}

								{mode === "signup" && (
									<form onSubmit={handleSignup} class="vstack gap-3">
										<p class="text-body-secondary small mb-0">
											Artbox is invite-only. Sign up with the email address your
											invite was sent to.
										</p>
										<div>
											<label class="form-label">Email</label>
											<input
												type="email"
												class="form-control"
												value={email}
												onInput={(e) =>
													setEmail((e.target as HTMLInputElement).value)
												}
												required
											/>
										</div>
										<div>
											<label class="form-label">Password</label>
											<input
												type="password"
												class="form-control"
												value={password}
												onInput={(e) =>
													setPassword((e.target as HTMLInputElement).value)
												}
												required
												minLength={4}
											/>
										</div>
										<button
											type="submit"
											class="btn btn-primary w-100"
											disabled={loading}
										>
											{submitIcon}
											<span>{loading ? "Working…" : "Sign up"}</span>
										</button>
									</form>
								)}

								{mode === "verify" && (
									<form onSubmit={handleVerify} class="vstack gap-3">
										<p class="text-body-secondary small mb-0">
											We sent a code to <strong>{pendingEmail}</strong>.
										</p>
										<div>
											<label class="form-label">Verification code</label>
											<input
												type="text"
												inputMode="numeric"
												class="form-control"
												value={code}
												onInput={(e) =>
													setCode((e.target as HTMLInputElement).value)
												}
												required
												maxLength={6}
											/>
										</div>
										<button
											type="submit"
											class="btn btn-primary w-100"
											disabled={loading}
										>
											{submitIcon}
											<span>{loading ? "Working…" : "Verify"}</span>
										</button>
										<button
											type="button"
											class="btn btn-outline-secondary w-100"
											onClick={handleResend}
											disabled={loading}
										>
											<i class="bi bi-arrow-repeat" aria-hidden="true" />
											<span>Resend code</span>
										</button>
									</form>
								)}

								{mode === "forgot" && (
									<form onSubmit={handleForgot} class="vstack gap-3">
										<div>
											<label class="form-label">Email</label>
											<input
												type="email"
												class="form-control"
												value={email}
												onInput={(e) =>
													setEmail((e.target as HTMLInputElement).value)
												}
												required
											/>
										</div>
										<button
											type="submit"
											class="btn btn-primary w-100"
											disabled={loading}
										>
											{submitIcon}
											<span>{loading ? "Working…" : "Send reset code"}</span>
										</button>
									</form>
								)}

								{mode === "reset" && (
									<form onSubmit={handleReset} class="vstack gap-3">
										<p class="text-body-secondary small mb-0">
											Enter the code we sent to <strong>{pendingEmail}</strong>{" "}
											and choose a new password.
										</p>
										<div>
											<label class="form-label">Reset code</label>
											<input
												type="text"
												inputMode="numeric"
												class="form-control"
												value={code}
												onInput={(e) =>
													setCode((e.target as HTMLInputElement).value)
												}
												required
												maxLength={6}
											/>
										</div>
										<div>
											<label class="form-label">New password</label>
											<input
												type="password"
												class="form-control"
												value={newPassword}
												onInput={(e) =>
													setNewPassword((e.target as HTMLInputElement).value)
												}
												required
												minLength={4}
											/>
										</div>
										<button
											type="submit"
											class="btn btn-primary w-100"
											disabled={loading}
										>
											{submitIcon}
											<span>{loading ? "Working…" : "Reset password"}</span>
										</button>
									</form>
								)}

								<div class="mt-3 text-center small">
									{mode === "login" && (
										<div class="vstack gap-1">
											<button
												type="button"
												class="btn btn-link p-0"
												onClick={() => goto("signup")}
											>
												Need an account? Sign up
											</button>
											<button
												type="button"
												class="btn btn-link p-0"
												onClick={() => goto("forgot")}
											>
												Forgot your password?
											</button>
										</div>
									)}
									{mode === "signup" && (
										<button
											type="button"
											class="btn btn-link p-0"
											onClick={() => goto("login")}
										>
											Already have an account? Log in
										</button>
									)}
									{(mode === "verify" ||
										mode === "forgot" ||
										mode === "reset") && (
										<button
											type="button"
											class="btn btn-link p-0"
											onClick={() => goto("login")}
										>
											Back to log in
										</button>
									)}
								</div>
							</div>
						</div>

						{/* The frontend bundle is identical in dev and prod (the Go
						    server embeds the same dist/), so this can't be stripped at
						    build time the way the backend route is (build tag `dev`,
						    see dev_routes.go). Gate on hostname instead: the panel only
						    renders on localhost, where the dev server runs. In prod the
						    route 404s anyway, so this is cosmetic defense, not a
						    security boundary. */}
						{(window.location.hostname === "localhost" ||
							window.location.hostname === "127.0.0.1") && (
							<details class="w-100 mt-4">
								<summary class="text-body-secondary small">Dev login</summary>
								<DevLoginForm onAuthed={onAuthed} />
							</details>
						)}

						<div class="text-center small text-body-secondary mt-4 pb-4">
							<a href="/terms" class="text-body-secondary me-3">
								Terms
							</a>
							<a href="/privacy" class="text-body-secondary">
								Privacy
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

// Minimal local-testing escape hatch (POST /api/dev/login, dev builds only).
// We don't probe for the route on page load — that would 404-spam in
// production builds — the submit itself is the probe: if the route doesn't
// exist, the request fails and we surface that as a normal error.
function DevLoginForm({ onAuthed }: { onAuthed: () => void }) {
	const [devEmail, setDevEmail] = useState("")
	const [devName, setDevName] = useState("")
	const [devError, setDevError] = useState("")
	const [devLoading, setDevLoading] = useState(false)

	const submitDevLogin = async (e: Event) => {
		e.preventDefault()
		setDevError("")
		setDevLoading(true)
		try {
			await api.devLogin(devEmail.trim(), devName.trim() || devEmail.trim())
			onAuthed()
		} catch (err) {
			setDevError(
				err instanceof Error
					? err.message
					: "Dev login isn't available in this build",
			)
		} finally {
			setDevLoading(false)
		}
	}

	return (
		<form class="vstack gap-2 mt-2" onSubmit={submitDevLogin}>
			<input
				type="email"
				class="form-control"
				placeholder="you@example.com"
				value={devEmail}
				onInput={(e) => setDevEmail((e.target as HTMLInputElement).value)}
				required
			/>
			<input
				type="text"
				class="form-control"
				placeholder="Name (optional)"
				value={devName}
				onInput={(e) => setDevName((e.target as HTMLInputElement).value)}
			/>
			{devError && (
				<div class="alert alert-danger py-2 small mb-0">{devError}</div>
			)}
			<button
				type="submit"
				class="btn btn-outline-secondary"
				disabled={devLoading}
			>
				{devLoading ? (
					<Spinner size={16} />
				) : (
					<i class="bi bi-terminal" aria-hidden="true" />
				)}
				<span>Dev sign in</span>
			</button>
		</form>
	)
}
